import { inject, signal, computed, Injectable } from '@angular/core';
import {
    Auth,
    OAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    onAuthStateChanged,
    signOut,
} from '@angular/fire/auth';
import { AuthorizationService } from './authorization.service';
import { UserContext } from '../../models/user-context';
import { ReplaySubject } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { StorageUtils } from '../../utils/StorageUtils';
import { CsrfService } from '../../services/csrf.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly auth = inject(Auth);
    private readonly csrfService = inject(CsrfService);

    /** Servicio BFF que obtiene el contexto del usuario */
    private readonly authorizationService = inject(AuthorizationService);

    private userContextSignal = signal<UserContext | null>(null);

    public userContext = this.userContextSignal.asReadonly();

    /**
     * Perfil del usuario extraído directamente de los claims del ID Token
     * (name, email, preferred_username), tal como los entrega Keycloak vía
     * Identity Platform. En este portal, `preferredUsername` corresponde al
     * RUT del cliente (es el username con el que se autentica en Keycloak).
     */
    public identityProfile = signal<{ name: string | null; email: string | null; preferredUsername: string | null } | null>(null);

    private authReadySubject = new ReplaySubject<boolean>(1); // ← guarda el último valor
    public authReady$ = this.authReadySubject.asObservable();

    constructor() {
        onAuthStateChanged(this.auth, (user) => {
            if (user) {
                this.checkAndLoadUserContext();
                this.loadIdentityProfile();
            } else {
                this.userContextSignal.set(null);
                this.identityProfile.set(null);
            }
        });
    }

    /**
     * Inicia el flujo de autenticación federada con Keycloak.
     * El modo (popup o redirección completa) se determina por
     * {@link environment.auth_environment.KEYCLOAK_LOGIN_MODE}. En modo redirect,
     * el resultado se recoge al volver a la app, en {@link handleIncomingAuth}.
     * @param state_url Url a la que se debe volver luego del login (guardada en sessionStorage).
     */
    async loginWithKeycloak(state_url?: string) {
        if (state_url) {
            StorageUtils.set(StorageUtils.STATE_KEY, state_url);
        }

        const provider = new OAuthProvider(environment.auth_environment.PROVIDER_KEYCLOAK_ID);

        if (environment.auth_environment.KEYCLOAK_LOGIN_MODE === 'popup') {
            await signInWithPopup(this.auth, provider);
            return;
        }

        await signInWithRedirect(this.auth, provider);
    }

    /**
     * Resuelve el resultado de un login por redirección (Keycloak) al volver a la app.
     * Debe invocarse en el `ngOnInit` de la pantalla de login.
     */
    async handleIncomingAuth() {
        const redirectResult = await getRedirectResult(this.auth);
        return redirectResult?.user ?? this.auth.currentUser;
    }

    /**
     * Cierra la sesión activa en Firebase y redirige físicamente al
     * end-session endpoint de Keycloak para asegurar la limpieza de la sesión federada.
     */
    async logout() {
        this.csrfService.clearCsrfCookie();
        this.userContextSignal.set(null);
        this.identityProfile.set(null);
        await signOut(this.auth);
        window.location.href = environment.auth_environment.KEYCLOAK_LOGOUT_URL;
    }

    activeAccount(): boolean {
        return !!this.auth.currentUser;
    }

    /**
     * Signal computada que mantiene un `Set` de permisos únicos del usuario.
     * Optimiza la verificación de seguridad permitiendo búsquedas en tiempo constante O(1).
     * Se recalcula automáticamente cuando el `userContext` cambia.
     * @returns {Set<string>} Conjunto de códigos de permisos (permits).
     */
    permissions = computed(() => {
        const user = this.userContext();
        if (!user || !user.groups) return new Set<string>();

        const allPermits = user.groups.flatMap((g: any) =>
            g.roles?.flatMap((r: any) => r.permits || []) || []
        );

        return new Set<string>(allPermits);
    });

    /**
     * Verifica de manera síncrona si el usuario posee una capacidad específica.
     * @param permission Código o lista de códigos de permisos a validar.
     * @returns {boolean} True si posee al menos un permiso (Lógica OR).
     */
    hasPermission(permission: string | string[]): boolean {
        const userPermissions = this.permissions();

        if (!userPermissions) return false;

        const permitsToCheck = Array.isArray(permission) ? permission : [permission];

        return permitsToCheck.some(p => userPermissions.has(p));
    }

    /**
     * Recupera el ID Token (JWT) vigente del usuario actual.
     * @returns {Promise<string | null>} Token JWT para cabeceras Authorization.
     */
    async getIdToken(): Promise<string | null> {
        const user = this.auth.currentUser;
        if (!user) return null;

        try {
            return await user.getIdToken();
        } catch (error) {
            console.warn('No se pudo obtener el ID Token', error);
            return null;
        }
    }

    /**
     * Carga la información de permisos y grupos desde el backend de autorización.
     */
    async loadUserContextData(): Promise<void> {
        const uid = this.getUid();
        const idpCode = environment.auth_environment.IDP_KEYCLOAK_CODE;
        await this.csrfService.csrf();
        return new Promise((resolve, reject) => {
            this.authorizationService
                .getUserContext(uid || '', idpCode)
                .subscribe({
                    next: (ctx) => {
                        this.userContextSignal.set(ctx.data.userContext);
                        this.authReadySubject.next(true);
                        resolve();
                    },
                    error: (err) => {
                        console.error('Falló en la invocación del servicio:', err);
                        reject(err);
                    }
                });
        });
    }

    /**
     * Recupera el identificador único del usuario (uid de Firebase).
     * @returns {string | null} El uid del usuario.
     */
    getUid(): string | null {
        return this.auth.currentUser?.uid || null;
    }

    /**
     * Método auxiliar para validar si tenemos usuario y cargar sus datos del BFF.
     */
    private async checkAndLoadUserContext() {
        const user = this.auth.currentUser;
        if (user && !this.userContext()) {
            await this.loadUserContextData();
        }
    }

    /**
     * Extrae name, email y preferred_username (RUT) del ID Token del usuario
     * actual y actualiza {@link identityProfile}.
     *
     * Busca primero en los claims de nivel superior del token (donde Firebase
     * suele mapear `name`/`email` de proveedores OIDC) y cae a
     * `firebase.sign_in_attributes` para claims custom (ej. `preferred_username`
     * de Keycloak) cuando el proveedor los expone solo ahí.
     */
    private async loadIdentityProfile() {
        const currentUser = this.auth.currentUser;
        if (!currentUser) {
            this.identityProfile.set(null);
            return;
        }

        const result = await currentUser.getIdTokenResult();
        const claims: any = result.claims ?? {};
        const signInAttributes = this.getByKey<any>(claims.firebase, 'sign_in_attributes') ?? {};

        this.identityProfile.set({
            name: claims['name'] ?? signInAttributes['name'] ?? null,
            email: claims['email'] ?? signInAttributes['email'] ?? currentUser.email ?? null,
            preferredUsername: claims['preferred_username'] ?? signInAttributes['preferred_username'] ?? null,
        });
    }

    /**
     * Recupera un atributo específico del claim de Firebase en el token.
     * @param key Clave del atributo solicitado.
     */
    private getByKey<T = unknown>(obj: unknown, key: string): T | undefined {
        if (!obj || typeof obj !== 'object') return undefined;
        return (obj as Record<string, T>)[key];
    }
}
